import { StyleSheet } from "@react-pdf/renderer";
import { colors, fontSize, spacing, borderRadius, FONT_FAMILY } from "./pdfTokens";

const styles = StyleSheet.create({
  page: {
    fontFamily: FONT_FAMILY,
    backgroundColor: colors.pageBackground,
    paddingTop: 64,
    paddingBottom: 44,
    paddingHorizontal: 36,
  },

  headerFixed: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 36,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLogo: {
    width: 72,
    height: 32,
    objectFit: "contain" as const,
  },
  headerDate: {
    fontSize: fontSize.sm,
    color: colors.textMuted,
    letterSpacing: 0.1,
  },

  footerFixed: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: 36,
    alignItems: "center",
  },
  footerText: {
    fontSize: fontSize.xs,
    color: colors.textMuted,
    letterSpacing: 0.2,
    textAlign: "center",
  },

  promptCard: {
    backgroundColor: colors.promptCardBg,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  promptText: {
    fontSize: fontSize.md,
    fontWeight: 500,
    color: colors.textPrimary,
    letterSpacing: 0.1,
  },

  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.md,
  },

  blocksContainer: {
    flexDirection: "column",
    gap: spacing.xl,
  },

  blockTitle: {
    fontSize: fontSize.lg,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
    letterSpacing: 0.1,
  },

  // Markdown text styles
  mdH1: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    marginTop: spacing.sm,
    letterSpacing: 0.2,
  },
  mdH2: {
    fontSize: fontSize.lg,
    fontWeight: 700,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
    letterSpacing: 0.1,
  },
  mdH3: {
    fontSize: fontSize.md,
    fontWeight: 700,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    marginTop: spacing.xs,
    letterSpacing: 0.1,
  },
  mdParagraph: {
    fontSize: fontSize.md,
    fontWeight: 400,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    lineHeight: 1.55,
    letterSpacing: 0.1,
  },
  mdBold: {
    fontWeight: 700,
    color: colors.textSecondary,
  },
  mdItalic: {
    fontStyle: "italic",
  },
  mdInlineCode: {
    backgroundColor: colors.tableHeaderBg,
    borderRadius: borderRadius.sm,
    fontSize: fontSize.sm,
    paddingHorizontal: spacing.xs,
    paddingVertical: 1,
  },
  mdListItem: {
    flexDirection: "row",
    marginBottom: 2,
    paddingLeft: 4,
  },
  mdBullet: {
    fontSize: fontSize.md,
    color: colors.textMuted,
    marginRight: spacing.sm,
    width: 12,
  },
  mdListText: {
    fontSize: fontSize.md,
    fontWeight: 400,
    color: colors.textSecondary,
    flex: 1,
    lineHeight: 1.55,
    letterSpacing: 0.1,
  },
  mdNestedList: {
    marginTop: spacing.xs,
  },
  mdBlockquote: {
    borderLeftWidth: 4,
    borderLeftColor: colors.textMuted,
    paddingLeft: spacing.lg,
    paddingVertical: spacing.sm,
    marginVertical: spacing.md,
    backgroundColor: colors.promptCardBg,
    borderRadius: borderRadius.sm,
    fontStyle: "italic",
  },
  mdHr: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.lg,
  },
  mdLink: {
    color: colors.link,
    textDecoration: "underline",
  },
  mdCodeBlock: {
    backgroundColor: colors.tableHeaderBg,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
  },
  mdCodeText: {
    fontSize: fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 1.45,
  },
  mdUnsupportedBlock: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.evenRowBg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
  },
  mdUnsupportedTitle: {
    fontSize: fontSize.sm,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 2,
  },
  mdUnsupportedText: {
    fontSize: fontSize.sm,
    color: colors.textMuted,
    lineHeight: 1.45,
  },

  // KPI card styles
  kpiGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  kpiCard: {
    borderWidth: 1,
    borderColor: colors.kpiCardBorder,
    backgroundColor: colors.kpiCardBg,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
  },
  kpiLabel: {
    fontSize: fontSize.sm,
    color: colors.textMuted,
    marginBottom: 2,
    letterSpacing: 0.1,
  },
  kpiValueRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: spacing.sm,
  },
  kpiValue: {
    fontSize: fontSize.titleSm,
    fontWeight: 700,
  },
  kpiDelta: {
    fontSize: fontSize.sm,
    fontWeight: 500,
  },

  // Table styles
  tableContainer: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    overflow: "hidden",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: colors.tableHeaderBg,
  },
  tableHeaderCell: {
    fontSize: fontSize.sm,
    fontWeight: 500,
    color: colors.textPrimary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    letterSpacing: 0.1,
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  tableRowEven: {
    backgroundColor: colors.evenRowBg,
  },
  tableCell: {
    fontSize: fontSize.sm,
    fontWeight: 400,
    color: colors.textSecondary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    letterSpacing: 0.1,
  },

  // Chart styles
  chartImage: {
    width: "100%",
    maxHeight: 340,
    objectFit: "contain" as const,
  },
});

export default styles;
